import React, { useEffect, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { cloneDeep } from 'lodash';
import { TbTrash, TbX } from 'react-icons/tb';
import { getPhotoDescription } from '../../helpers/getPhotoDescription';
import { UploadFileTypes } from '../../constants/UploadFileTypes';
import { addPhoto } from '../VehicleActions';
import DragAndDropPhoto from './drag/DragAndDropPhoto';
import CommonLoader from '../../common/components/CommonLoader';
import { checkInteriorPerspective } from '../../helpers/checkInteriorPerspective';

function VehiclePhotoEditForm({ onCloseAlert, photos, onSave, photoEditIndex }) {
  const dispatch = useDispatch();

  const [editingPhoto, setEditingPhoto] = useState({});
  const [photosToDelete, setPhotosToDelete] = useState([]);
  const [photoLoading, setPhotoLoading] = useState(false);

  const isNewPhoto = photoEditIndex === -1;

  useEffect(() => {
    const newPhoto = getPhotoToEditOrCreateNew(photoEditIndex);
    setEditingPhoto(cloneDeep(newPhoto));
    setPhotosToDelete([]);
  }, [photoEditIndex]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        onCloseAlert();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onCloseAlert]);

  const getPhotoToEditOrCreateNew = (photoEditIndex) => {
    if (photoEditIndex === null) return null;
    if (photoEditIndex === -1) {
      return {
        fileUrl: null,
        mediaMetadataId: null,
        damages: [],
        damagesToDelete: [],
        sortNumber: (photos?.length ?? 0) + 1 || 1,
        perspective: getPhotoDescription((photos?.length ?? 0) + 1 || 1).PERSPECTIVE,
      };
    }
    if (photoEditIndex < photos?.length) {
      return photos[photoEditIndex];
    }
    return null;
  };

  const setPhoto = async (file) => {
    if (file && file?.type.includes(UploadFileTypes.IMAGE)) {
      setPhotoLoading(true);
      const photo = await dispatch(addPhoto(file));
      // guard against failed/undefined upload responses
      if (!photo || !photo.id) {
        setPhotoLoading(false);
        return;
      }
      setEditingPhoto((prevState) => ({
        ...prevState,
        mediaMetadataId: photo.id,
        fileUrl: photo.fileUrl,
        urlTemp: URL.createObjectURL(file),
      }));
      setPhotoLoading(false);
    }
  };

  const savePhotos = () => {
    onSave(editingPhoto, photosToDelete);
  };

  const allDamagesHasSelectedTypes = (photo) => {
    if (!photo) return false;
    if (!photo.damages) return true;
    if (checkInteriorPerspective(photo.perspective)) return true;
    if (photo.damages.length === 0) return true;
    return !photo.damages.find((item) => !item.damageType);
  };

  const deletePhoto = () => {
    setPhotosToDelete((prevState) => [...prevState, editingPhoto.mediaMetadataId]);
    setEditingPhoto((prev) => ({
      ...prev,
      fileUrl: null,
      mediaMetadataId: null,
    }));
  };

  return (
    <Container className="d-flex flex-column justify-content-center text-center pt-3">
      <button type="button" className="photo_gallery_close_button" onClick={onCloseAlert}>
        <TbX size={24} />
      </button>
      <Row className=" d-flex justify-content-center">
        <Col lg="6" md="8" sm="12">
          <Row>
            <Col className="vehicle_details_edit_photo__labels mb-2">
              {isNewPhoto ? <h4>New Photo</h4> : <h4>Edit Photo</h4>}
              <p>{getPhotoDescription(editingPhoto.sortNumber).DESCRIPTION}</p>
            </Col>
          </Row>
          <Row className="mb-4">
            {editingPhoto.fileUrl ? (
              <Col className="vehicle_details__added-photo-container">
                <img alt="Preview car" src={editingPhoto.fileUrl} className="added-photo" />
                <button
                  type="button"
                  className="vehicle_details__edit_photo_delete_icon"
                  onClick={deletePhoto}
                >
                  <TbTrash color="red" size={24} />
                </button>
              </Col>
            ) : (
              <Col>
                {photoLoading ? (
                  <CommonLoader />
                ) : (
                  <DragAndDropPhoto
                    title="Click here to upload vehicle images"
                    onDrop={setPhoto}
                  />
                )}
              </Col>
            )}
          </Row>
          <Row>
            <Col
              className="d-flex flex-column align-items-md-start align-items-sm-center"
              sm="12"
              md="6"
              lg="6"
            >
              <Button
                className="mt-4 float-end rounded-pill btn-soft-primary"
                onClick={onCloseAlert}
              >
                Close
              </Button>
            </Col>
            <Col
              className="d-flex flex-column align-items-md-end align-items-sm-center"
              sm="12"
              md="6"
              lg="6"
            >
              <Button
                className="rounded-pill mt-4 float-end btn-primary"
                disabled={!allDamagesHasSelectedTypes(editingPhoto)}
                onClick={savePhotos}
              >
                Save
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
}

export default VehiclePhotoEditForm;

VehiclePhotoEditForm.propTypes = {
  onCloseAlert: PropTypes.func,
  photos: PropTypes.array,
  photoEditIndex: PropTypes.number,
};

VehiclePhotoEditForm.defaultProps = {
  photos: [],
  onCloseAlert: () => {},
  photoEditIndex: null,
};
